import * as Sentry from '@sentry/react';

// GlitchTip (Sentry protocol) telemetry - replaces the App Insights browser
// SDK (#134). No DSN = telemetry fully disabled (mirrors the backend
// contract). Unhandled errors and rejected promises are captured by the
// SDK's global handlers once initialised.

export const isTelemetryEnabled = () => Boolean(import.meta.env.VITE_GLITCHTIP_DSN);

export const initTelemetry = () => {
  if (!isTelemetryEnabled()) return;

  Sentry.init({
    dsn: import.meta.env.VITE_GLITCHTIP_DSN,
    environment: import.meta.env.VITE_STAGE || 'unknown',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_GLITCHTIP_TRACES_SAMPLE_RATE || '1.0'),
    // No cross-origin trace headers yet: the Classic API's deployed CORS
    // policy has not been verified to allow sentry-trace/baggage, and a
    // rejected preflight would break the API call itself. Enable after the
    // CORS check (tracked in docs/12-glitchtip-observability.md).
    tracePropagationTargets: [],
    // GlitchTip's searchable-logs feature ingests these. Its Logs-view
    // Service filter binds to the OTel service.name attribute per record.
    enableLogs: true,
    beforeSendLog(log) {
      return { ...log, attributes: { ...log.attributes, 'service.name': 'classic-web' } };
    },
    // Redaction policy (#134).
    sendDefaultPii: false,
    beforeSend(event) {
      const { request } = event;
      if (request) {
        delete request.cookies;
        const { headers } = request;
        if (headers) {
          delete headers.Authorization;
          delete headers.authorization;
          delete headers.Cookie;
          delete headers.cookie;
        }
      }
      return event;
    },
  });
};

const toAttributes = properties =>
  Object.fromEntries(
    Object.entries(properties || {}).map(([key, value]) => [
      key,
      typeof value === 'string' ? value : JSON.stringify(value ?? null),
    ]),
  );

// Replaces the App Insights useTrackEvent sink: each action becomes a
// searchable GlitchTip structured log plus a breadcrumb on any later error.
export const trackKwelloAction = (label, properties) => {
  if (!isTelemetryEnabled()) return;

  const attributes = toAttributes(properties);
  Sentry.addBreadcrumb({
    category: 'kwello.action',
    message: label,
    level: 'info',
    data: attributes,
  });

  // Pageviews are breadcrumb-only: Classic already ships FullStory, Pendo
  // and Intercom for usage analytics, so GlitchTip only needs them as the
  // error trail - not as standalone log records (#134 noise policy).
  if (label === 'pageview') return;

  const url = properties?.kwelloURL ? ` ${properties.kwelloURL}` : '';
  Sentry.logger.info(`${label}${url}`, attributes);
};

// Report a swallowed failure as a GlitchTip issue. Without this, a form that
// catches its own error is invisible to observability as well as to the user —
// which is how the 2026-09-25 GKP outage stayed unexplained for a week
// (docs/13-gkp-edit-profile-incident.md).
export const captureTelemetryException = (error, context) => {
  if (!isTelemetryEnabled()) return;
  Sentry.captureException(error, context ? { extra: toAttributes(context) } : undefined);
};

export const identifyTelemetryUser = user => {
  if (!isTelemetryEnabled()) return;
  Sentry.setUser(user && (user.id || user.email) ? { id: user.id, email: user.email } : null);
};
